import * as models from "@common/models/v2";
import type * as schemas from "@common/schemas/v2";
import { BaseStep } from "./base";
import { ElementClicker, ElementTracker } from "@/lib/element";
import { StepActions } from "./step";
import { makeObservable, observable, override } from "mobx";
import { FlowFlexibility } from "@common/schemas";

export class AutoClickStep extends BaseStep {
  attributes: models.AutoClickStepAttributes;
  actions: StepActions;

  target?: Element | null;
  isClicked = false;
  elementClicker: ElementClicker;
  elementTracker?: ElementTracker;
  stopTrackingTargetElement?: () => void;

  constructor(step: schemas.Step, actions: StepActions, flowFlexibility: FlowFlexibility) {
    super(step, actions, flowFlexibility);
    this.attributes = new models.AutoClickStepAttributes(step.attributes as schemas.AutoClickStepAttributes);
    this.actions = actions;
    this.elementClicker = new ElementClicker();
    makeObservable(this, {
      isClicked: observable,
      isBlocking: override,
    });
  }

  async run() {
    super.run();
    this.isClicked = false;
    this.trackTargetElement();
  }

  private trackTargetElement() {
    this.stopTrackingTargetElement?.();
    this.stopTrackingTargetElement = this.actions.onTargetElementChange(
      this.id,
      this.handleTargetElementChange.bind(this)
    );
  }

  private handleTargetElementChange(targetElement: Element | null) {
    this.target = targetElement;
    this.actions.setCurrentTargetElement(targetElement);
  }

  async finish() {
    super.finish();
    await this.clickElement(this.attributes.targetElementSelector);
  }

  dispose(): void {
    super.dispose();
    this.stopTrackingTargetElement?.();
    this.stopTrackingTargetElement = undefined;
    this.elementTracker?.stop();
    this.target = undefined;
    this.actions.setCurrentTargetElement(undefined);
  }

  // Why not accept the element as a parameter directly?
  private async clickElement(selector: string) {
    if (!this.isClicked) {
      await this.elementClicker.clickElement(selector);
      this.elementTracker?.stop();
      this.isClicked = true;
    }
  }

  get isBlocking() {
    return !this.isClicked;
  }

  get isBlockingStepType() {
    return true;
  }

  get isTargetableStepType() {
    return true;
  }
}
